// ── 출퇴근 관리 탭 화면의 입구 (2026-09-29) ─────────────────────────────────────
//   본사  /admin/attendance            ← 옛 AttendanceRoute(AppRoutes.guards) + /admin/attendance-calendar
//   회사별 /admin/ext/:code/attendance  ← 옛 ExternalCompanyAttendance + ExternalCompanyAttendanceCalendar
//   각 탭에 넘기는 값은 옛 화면들이 넘기던 값 그대로다(회사값·API 주소 무변경 → 보이는 직원 범위도 그대로).
import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAppPermissions } from '../../../hooks/useAppPermissions';
import { useExtCompany, ExtCompanyFallback } from '../useExtCompany';
import AttendanceManagement from '../AttendanceManagement';
import AttendanceCalendar from '../AttendanceCalendar';
import AttendanceHub from './AttendanceHub';
import { hqAttendanceTabs, extAttendanceTabs } from './attendanceTabs';

const loadingView = <div style={{ padding: 24 }}>로딩 중...</div>;

// ☰ 'IP 관리' 범위(2026-10-23) — 본사 = 본사 출퇴근 목록과 같은 회사, 회사별 = 그 회사(서버 routes/ip-scope.route.js)
const HQ_IP_SCOPE = { apiBase: '/api/admin/ip-scope/hq' };

// 본사. 회사값 = 뷰어의 회사(운영자=본사 / 테넌트 관리자=자기 회사), 모르면 이 사이트 본사(/api/me hqCompanyCode, 2026-10-30 — 예전 'PINE').
//   (값을 안 넘기면 AttendanceManagement 가 'PINE' 기본값을 써서 시간 설정에 PINE 카드가 뜨던 누출)
export function HqAttendanceHub() {
  const { me, loaded, permCodes } = useAppPermissions();
  if (!loaded) return loadingView;
  const companyCode = me?.company?.code || me?.hqCompanyCode || 'PINE';
  return (
    <AttendanceHub
      tabs={hqAttendanceTabs(permCodes)}
      renderCalendar={(p) => (
        <AttendanceCalendar
          title={p.title} subtitle={p.subtitle} tabSlot={p.tabSlot} onPickPerson={p.onPickPerson}
          tools={p.withTools ? { companyCode } : null} reloadSignal={p.reloadSignal}
          ipScope={HQ_IP_SCOPE}
        />
      )}
      renderDetail={(p) => (
        <AttendanceManagement
          companyCode={companyCode} pageTitle={p.title} subtitle={p.subtitle} tabSlot={p.tabSlot}
          initialUserId={p.initialUserId} initialYm={p.initialYm}
          overrideApiBase="/api/admin/attendance-calendar" onDataChanged={p.onDataChanged}
          ipScope={HQ_IP_SCOPE}
        />
      )}
    />
  );
}

// 회사별. 회사 확인은 옛 두 화면과 같은 검사(출퇴근 기능 켜짐) — 탭은 그 회사 열쇠(accessible_features)로.
const CHECK = { key: 'attendance', label: '출퇴근 관리', mode: 'truthy' };

export function ExtAttendanceHub() {
  const { code } = useParams();
  const { company, loading, error } = useExtCompany(code, CHECK);
  if (!company) return <ExtCompanyFallback loading={loading} error={error} />;
  const c = company.code;
  const ipScope = { apiBase: `/api/admin/ip-scope/company/${encodeURIComponent(c)}` };
  return (
    <AttendanceHub
      key={c}
      tabs={extAttendanceTabs(company.accessible_features)}
      renderCalendar={(p) => (
        <AttendanceCalendar
          key={c}
          calendarApiBase={`/api/admin/ext/${c}/attendance-calendar`}
          title={p.title} subtitle={p.subtitle} tabSlot={p.tabSlot} onPickPerson={p.onPickPerson}
          tools={p.withTools
            ? { attendancePath: `/api/admin/ext/${c}/attendance`, companyCode: c, apiBasePath: `/api/admin/ext/${c}` }
            : null}
          reloadSignal={p.reloadSignal}
          ipScope={ipScope}
        />
      )}
      renderDetail={(p) => (
        <AttendanceManagement
          key={c}
          attendancePath={`/api/admin/ext/${c}/attendance`}
          companyCode={c}
          apiBasePath={`/api/admin/ext/${c}`}
          pageTitle={p.title} subtitle={p.subtitle} tabSlot={p.tabSlot}
          initialUserId={p.initialUserId} initialYm={p.initialYm}
          overrideApiBase={`/api/admin/ext/${c}/attendance-calendar`} onDataChanged={p.onDataChanged}
          ipScope={ipScope}
        />
      )}
    />
  );
}

// 예전 달력 주소(즐겨찾기·위젯·휴대폰 바로가기) → 합친 화면의 달력 탭
export function HqAttendanceCalendarRedirect() {
  return <Navigate to="/admin/attendance?view=calendar" replace />;
}

export function ExtAttendanceCalendarRedirect() {
  const { code } = useParams();
  return <Navigate to={`/admin/ext/${encodeURIComponent(code || '')}/attendance?view=calendar`} replace />;
}
